import React, { useEffect, useRef, useState } from 'react';
import P from 'prop-types';
import classNames from 'classnames';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { CenteredContainer } from '@nerdwallet/currency';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import editingConfig from './horizontal-banner-editing-config';
import BannerHorizontalContent from './horizontal-banner-content';
import './horizontal-banner.less';
import { useHorizontalBanner, defaultProps } from './use-horizontal-banner';
import { updateComponent } from '../../containers/builder/actions';
import UniversalText from '../universal-text/universal-text';
import UniversalButton from '../universal-button/universal-button';
import UniversalAuthButton from '../universal-auth-button/universal-auth-button';

const ContentContainer = ({
  fullWidth,
  children,
  centeredInParentBackgroundProps,
}) => {
  if (fullWidth) {
    return children;
  }
  return (
    <UniversalContainer
      {...centeredInParentBackgroundProps}
      data-testid="centeredInParentBackgroundContainer"
    >
      <CenteredContainer>{children}</CenteredContainer>
    </UniversalContainer>
  );
};

const HorizontalBanner = ({
  container,
  title,
  subtitle,
  actionButton,
  authButton,
  disableDefaultProps,
  includeSignUpButtons,
  includeContentWidthLimitation,
  includeParentWidthLimitation,
  isSignedIn,
  centeredInParentBackground,
  flatOutline,
  selectedComponentId,
  updateComponentProps,
  isCopy,
}) => {
  const currentComponent = flatOutline?.[selectedComponentId];
  const emptyPropsRef = useRef();
  const [isLoadedTemplate, setIsLoadedTemplate] = useState(null);

  const { titleProps, subtitleProps, containerProps, actionButtonProps } =
    useHorizontalBanner({
      title,
      subtitle,
      container,
      actionButton,
      disableDefaultProps,
    });

  useEffect(() => {
    if (
      !isLoadedTemplate &&
      !disableDefaultProps &&
      currentComponent &&
      !currentComponent?.props?.gridItems &&
      !isCopy
    ) {
      setIsLoadedTemplate(true);
      emptyPropsRef.current = currentComponent;
      updateComponentProps({
        ...currentComponent,
        props: { ...defaultProps },
      });
    }
  }, [
    currentComponent,
    updateComponentProps,
    isLoadedTemplate,
    subtitleProps,
    containerProps,
    disableDefaultProps,
    isCopy,
  ]);

  useEffect(() => {
    if (disableDefaultProps) {
      updateComponentProps({
        ...emptyPropsRef.current,
        props: { disableDefaultProps: true },
      });
      setIsLoadedTemplate(null);
    }
  }, [disableDefaultProps, updateComponentProps]);

  return includeSignUpButtons && isSignedIn ? null : (
    <Impression
      eventType="element_impression"
      entityName="horizontal_banner"
      className={classNames('horizontal-banner__wrapper')}
    >
      <ContentContainer
        fullWidth={!includeParentWidthLimitation}
        centeredInParentBackgroundProps={centeredInParentBackground}
      >
        <UniversalContainer
          {...containerProps}
          data-testid="horizontalBannerContainer"
        >
          <BannerHorizontalContent
            actionButtonProps={actionButtonProps}
            authButton={authButton}
            includeContentWidthLimitation={includeContentWidthLimitation}
            includeSignUpButtons={includeSignUpButtons}
            isSignedIn={isSignedIn}
            subtitleProps={subtitleProps}
            titleProps={titleProps}
          />
        </UniversalContainer>
      </ContentContainer>
    </Impression>
  );
};

ContentContainer.propTypes = {
  fullWidth: P.bool,
  children: P.node,
  centeredInParentBackgroundProps: universalContainerPropTypes,
};

HorizontalBanner.displayName = 'Horizontal Banner';

HorizontalBanner.propTypes = {
  centeredInParentBackground: universalContainerPropTypes,
  disableDefaultProps: P.bool,
  children: P.node,
  flatOutline: P.object,
  selectedComponentId: P.string,
  updateComponentProps: P.func,
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  actionButton: UniversalButton.propTypes,
  authButton: UniversalAuthButton.propTypes,
  includeSignUpButtons: P.bool,
  includeContentWidthLimitation: P.bool,
  includeParentWidthLimitation: P.bool,
  isSignedIn: P.bool,
  isCopy: P.bool,
};

HorizontalBanner.defaultProps = {
  includeSignUpButtons: false,
  includeContentWidthLimitation: false,
  includeParentWidthLimitation: false,
  centeredInParentBackgroundProps: {},
  disableDefaultProps: false,
  children: null,
};

HorizontalBanner.getEditingConfig = editingConfig;
const mapDispatchToProps = (dispatch) => ({
  updateComponentProps: (componentId) => {
    dispatch(updateComponent(componentId));
  },
});

export default connect(
  createStructuredSelector({
    size: (state) => state?.mediaQuery,
    isSignedIn: (state) => state?.identity?.isSignedIn,
    selectedComponentId: (state) => state?.builder?.selected, // get Editor Panel selected component
    flatOutline: (state) => state?.builder?.flatOutline, // get Editor Panel current component data
  }),
  mapDispatchToProps
)(HorizontalBanner);
